import axios from 'axios';
import { Circle, CircleX, DeleteIcon, Edit, EditIcon, Save } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

interface LevelItem {
  level: number;
  total_bet_amt: number;
}

const Level = () => {
  const [levels, setLevels] = useState<LevelItem[]>([]);
  const [editLevel, setEditLevel] = useState<LevelItem | null>(null);
  const navigate = useNavigate();
  const [newLevel, setNewLevel] = useState<LevelItem>({
      level: 0,
      total_bet_amt: 0,
    });

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    navigate('/login');
  };

  // Fetch levels from the API
  const fetchLevels = async () => {
    try {
      const response = await axios.get('https://api.trubetx.com/admin/getLevel');
      if (response.data) {
        setLevels(response.data.data);
      } else {
        toast('Failed to fetch levels.');
      }
    } catch (error) {
      console.error('Error fetching levels:', error);
      toast('Error fetching levels.');
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  // Handle input changes for editing levels
  const handleEditLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editLevel) {
      const { name, value } = e.target;
      setEditLevel((prevLevel) => ({
        ...prevLevel!,
        [name]: value ? parseFloat(value) : 0,
      }));
    }
  };

    // Handle input changes for new bonus
    const handleNewBonusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setNewLevel((prevBonus) => ({
        ...prevBonus,
        [name]: value ? parseInt(value) : 0,
      }));
    };

  // Update an existing level
  const handleUpdateLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editLevel) {
      try {
        const response = await axios.post(
          `https://api.trubetx.com/admin/updateLevel/${editLevel.level}`,
          editLevel
        );
        if (response.data) {
          toast('Level updated successfully!');
          setEditLevel(null); // Clear the edit state
          fetchLevels();
        } else {
          toast('Failed to update level.');
        }
      } catch (error) {
        console.error('Error updating level:', error);
        toast('Error updating level.');
      }
    }
  };

  // Delete a level
  const handleDeleteLevel = async (id: number) => {
    try {
      const response = await axios.delete(`https://api.trubetx.com/admin/deleteLevel/${id}`);
      if (response.data) {
        toast('Level deleted successfully!');
        fetchLevels();
      } else {
        toast('Failed to delete level.');
      }
    } catch (error) {
      console.error('Error deleting level:', error);
      toast('Error deleting level.');
    }
  };

  const handleAddLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLevel?.level || !newLevel?.total_bet_amt) {
      toast('Please provide valid inputs for the new level.');
      return;
    }

    try {
      const response = await axios.post(
        'https://api.trubetx.com/admin/createLevel',
        newLevel
      );
      if (response.data.status) {
        toast('Level added successfully!');
        setNewLevel({
          level: 0,
          total_bet_amt: 0,
        });
        fetchLevels();
      } else {
        toast('Failed to add deposit bonus.');
      }
    } catch (error : any) {
      console.error('Error adding deposit bonus:', error);
      toast(error.response.data.message);
    }
  };

  const filteredLevels = levels.filter(level => level.level > -1);

  return (
    <div className="ml-0 bg-white p-4">
      <h2 className="text-2xl font-semibold mb-4 text-black">Add New User Level</h2>
      <hr className="p-5" />

      <div className='mb-8'>
        <form onSubmit={handleAddLevel} className='space-y-4'>
          <div>
            <label className='text-black'>Level</label>
            <input
              type='number'
              name='level'
              value={newLevel.level}
              onChange={handleNewBonusChange}
              className='border rounded p-2 w-full'
            />
          </div>
          <div>
            <label className='text-black'>Total Bet Amount</label>
            <input
              type='number'
              name='total_bet_amt'
              value={newLevel.total_bet_amt}
              onChange={handleNewBonusChange}
              className='border rounded p-2 w-full'
            />
          </div>

          <button
            type="submit"
            // onClick={handleUpdateLevel}
            className="flex items-center gap-2 text-sm hover:text-blue-500 hover:scale-105 transition-transform  p-2 text-gray-700"
             
          >
            <Save size={20} />
            <span> Add Level</span>
          </button>
                  
         
        </form>
      </div>

      {/* Edit Existing Level Form */}
      {editLevel && (
        <div className="mb-8">
          <h3 className="text-xl text-black">Edit Level</h3>
          <form onSubmit={handleUpdateLevel} className="space-y-4">
            
            <div>
              <label className="text-black">Level</label>
              <input
                type="number"
                name="level"
                value={editLevel?.level}
                disabled
                onChange={handleEditLevelChange}
                className="border bg-gray-500 rounded p-2 w-full"
              />
            </div>
            <div>
              <label className="text-black">Total Bet Amount</label>
              <input
                type="number"
                name="total_bet_amt"
                value={editLevel?.total_bet_amt}
                onChange={handleEditLevelChange}
                className="border rounded p-2 w-full"
              />
            </div>

           <div className="flex justify-start gap-10 items-center">
  <button
    type="submit"
    onClick={handleUpdateLevel}
    className="flex items-center gap-2 text-sm hover:text-blue-500 hover:scale-105 transition-transform"
  >
    <Save size={20} />
    <span>Save</span>
  </button>

  <button
    type="button"
    onClick={() => setEditLevel(null)}
    className="flex items-center gap-2 text-sm hover:text-red-500 transition-colors hover:scale-105 "
  >
    <CircleX size={20} />
    <span>Cancel</span>
  </button>
</div>


          </form>
        </div>
      )}

      {/* Display Levels */}
      <div>
        {levels.length > 0 ? (
          <table className="table-auto w-full text-black text-center">
            <thead>
              <tr>
                <th className="border px-4 py-2">Level</th>
                <th className="border px-4 py-2">Total Bet Amount</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              
              {filteredLevels.map((level) => (
                <tr key={level.level}>
                  <td className="border px-4 py-2">{level.level}</td>
                  <td className="border px-4 py-2">{level.total_bet_amt}</td>
                 
                   <td className="border px-4 py-2 flex justify-center items-center gap-4">
                     <button
                    onClick={() => setEditLevel(level)}
                      className="flex items-center gap-2 text-sm hover:text-blue-500 hover:scale-105 transition-transform  p-2 text-gray-700"
                    >
                      <Edit size={20} />
                      <span> Edit</span>
                    </button>
                     <button
                     onClick={() => handleDeleteLevel(level.level)}
                      className="flex items-center gap-2 text-sm hover:text-red-500 hover:scale-105 transition-transform  p-2 text-gray-700"
                    >
                      <DeleteIcon size={20} />
                      <span> Delete</span>
                    </button>
                   
                   
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-black">No levels available at the moment.</p>
        )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default Level;
